import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Box, Button, Link, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useState } from "react";
import { ItemProperty, UserAutocomplete } from "src/components";
import { Links } from "src/constants";
import { UserDto } from "src/http";

interface IProps {
  owners: UserDto[];
  primaryOwner?: UserDto | null;
  userCanEdit: boolean;
  disabled?: boolean;
  onSaveClicked?: (updatedOwners: UserDto[], removedOwners: UserDto[], primaryOwner: UserDto | null) => void;
  onCancelClicked?: () => void;
}

const OwnershipProperty = (props: IProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>(props.owners);
  const [primaryOwner, setPrimaryOwner] = useState<UserDto | null>(props.primaryOwner ?? null);

  function handleEditClicked() {
    setSelectedUsers(props.owners);
    setPrimaryOwner(props.primaryOwner ?? null);
    setIsEditing(true);
  }

  function handleSaveClicked() {
    const removedOwners = props.owners.filter(
      (originalOwner) => !selectedUsers.some((updatedOwner) => updatedOwner.userId === originalOwner.userId),
    );

    setIsEditing(false);
    props.onSaveClicked?.(selectedUsers, removedOwners, primaryOwner);
  }

  function handleCancelClicked() {
    setIsEditing(false);
    props.onCancelClicked?.();
  }

  function handleSelectedUsersChanged(users: UserDto[]) {
    setSelectedUsers(users);

    if (primaryOwner && (!users.some((u) => u.userId === primaryOwner.userId) || users.length <= 1)) {
      setPrimaryOwner(null);
    }
  }

  return (
    <ItemProperty
      labelIcon={<GroupOutlinedIcon />}
      label="Ownership"
      labelProps={{ sx: { top: 1 } }}
      canEdit={props.userCanEdit}
      isEditing={isEditing}
      onEditClicked={handleEditClicked}
    >
      <>
        {isEditing ? (
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <UserAutocomplete
              value={selectedUsers}
              onChange={(value) => handleSelectedUsersChanged(value)}
              placeholder="Add owners..."
              autofocus
              multiple
            />

            <Stack>
              <Typography variant="label" color={selectedUsers.length <= 1 ? "text.disabled" : "inherit"}>
                Primary Owner
              </Typography>
              <UserAutocomplete
                options={selectedUsers}
                value={primaryOwner}
                onChange={setPrimaryOwner}
                placeholder="Select a primary owner..."
                disabled={selectedUsers.length <= 1}
              />
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
              <Button variant="text" disabled={props.disabled} onClick={handleCancelClicked}>
                Cancel
              </Button>
              <Button variant="tertiary" disabled={props.disabled} onClick={handleSaveClicked}>
                Save
              </Button>
            </Stack>
          </Stack>
        ) : props.owners.length > 0 ? (
          <Stack spacing={0.5}>
            {props.primaryOwner && (
              <Typography variant="body2">
                <Link onClick={(e) => e.stopPropagation()} href={Links.User(props.primaryOwner.userId)}>
                  {props.primaryOwner.displayName}
                  {props.primaryOwner.enabled ? "" : " (deactivated)"}
                </Link>
              </Typography>
            )}
            <Typography variant="body1">
              {props.owners
                .filter((owner) => !props.primaryOwner || owner.userId !== props.primaryOwner.userId)
                .sort((a, b) => a.displayName.localeCompare(b.displayName))
                .map((owner, index) => (
                  <Box key={index} component="span">
                    {index > 0 ? ", " : ""}
                    <Link onClick={(e) => e.stopPropagation()} href={Links.User(owner.userId)}>
                      {owner.displayName}
                      {owner.enabled ? "" : " (deactivated)"}
                    </Link>
                  </Box>
                ))}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="placeholder">{props.userCanEdit ? "Assign ownership..." : "No owner"}</Typography>
        )}
      </>
    </ItemProperty>
  );
};

export { OwnershipProperty };
